export const AVATAR_OUTPUT_SIZE = 512;
export const MAX_AVATAR_BYTES = 10 * 1024 * 1024;
export const MAX_AVATAR_PIXELS = 20_000_000;
export function cropGeometry(width, height, zoom = 1, x = 0, y = 0) {
  if (!(width > 0 && height > 0)) throw new Error('Не удалось прочитать фотографию.');
  const z = Math.max(1, Math.min(3, Number(zoom) || 1));
  const size = Math.min(width, height) / z;
  const clamped = value => Math.max(-1, Math.min(1, Number(value) || 0));
  return { x: (width - size) / 2 * (1 + clamped(x)),
    y: (height - size) / 2 * (1 + clamped(y)), size };
}
export function cropAvatar(image, zoom, x, y) {
  const area = cropGeometry(image.naturalWidth, image.naturalHeight, zoom, x, y);
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_OUTPUT_SIZE; canvas.height = AVATAR_OUTPUT_SIZE;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Браузер не смог подготовить фотографию.');
  context.fillStyle = '#ffffff'; context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, area.x, area.y, area.size, area.size, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob)
    : reject(new Error('Не удалось подготовить фотографию.')), 'image/jpeg', 0.9));
}

// Read dimensions from the encoded header before the browser allocates pixels.
// The backend independently validates the complete raster after upload.
export async function validateAvatarFile(file) {
  if (!file || file.size > MAX_AVATAR_BYTES) throw new Error('Фотография должна быть не больше 10 МБ.');
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('Выберите фото JPG, PNG или WebP.');
  const bytes = new Uint8Array(await file.arrayBuffer()), view = new DataView(bytes.buffer);
  const matches = (offset, text) => [...text].every((char, index) => bytes[offset + index] === char.charCodeAt(0));
  let width, height;
  if (bytes.length >= 24 && bytes[0] === 0x89 && matches(1, 'PNG\r\n\x1a\n') && matches(12, 'IHDR')) {
    width = view.getUint32(16); height = view.getUint32(20);
  } else if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    while (offset + 3 < bytes.length) {
      if (bytes[offset++] !== 0xff) break;
      while (bytes[offset] === 0xff) offset++;
      const marker = bytes[offset++];
      if (marker === 0xd9 || marker === 0xda) break;
      if (marker === 0x01 || marker >= 0xd0 && marker <= 0xd8) continue;
      if (offset + 2 > bytes.length) break;
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) break;
      if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker) && length >= 7) {
        height = view.getUint16(offset + 3); width = view.getUint16(offset + 5); break;
      }
      offset += length;
    }
  } else if (bytes.length >= 30 && matches(0, 'RIFF') && matches(8, 'WEBP')) {
    if (matches(12, 'VP8X')) {
      const u24 = offset => bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16;
      width = 1 + u24(24); height = 1 + u24(27);
    } else if (matches(12, 'VP8 ') && bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a) {
      width = view.getUint16(26, true) & 0x3fff; height = view.getUint16(28, true) & 0x3fff;
    } else if (matches(12, 'VP8L') && bytes[20] === 0x2f) {
      width = 1 + ((bytes[21] | bytes[22] << 8) & 0x3fff);
      height = 1 + ((bytes[22] >> 6 | bytes[23] << 2 | bytes[24] << 10) & 0x3fff);
    }
  }
  if (!width || !height) throw new Error('Не удалось прочитать фото. Выберите JPG, PNG или WebP.');
  if (width * height > MAX_AVATAR_PIXELS || Math.min(width, height) < 16) {
    throw new Error('Выберите фотографию до 20 мегапикселей, не меньше 16 × 16 пикселей.');
  }
  return { width, height };
}
